// The `rrdtool xport --json --showtime` files that scripts/export.sh uploads (served as /data/<name>.json):
// meta.legend names the columns, every data row is [epochSeconds, v1, v2, …] with null for missing values
// (row t holds the mean over the step ending at t).

export interface RrdFile {
  meta: { legend: string[]; start?: number; end?: number; step?: number };
  data: unknown[][];
}

// [epoch ms, value]; null = no data (breaks the line).
export type Point = [number, number | null];

export function isRrdFile(x: unknown): x is RrdFile {
  const f = x as RrdFile;
  return !!f && typeof f === 'object' && Array.isArray(f.meta?.legend) && Array.isArray(f.data);
}

const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// The column with this legend (undefined when the file doesn't have it, e.g. an older export).
export function rrdColumn(file: RrdFile | undefined, legend: string, map?: (v: number) => number): Point[] | undefined {
  const i = file?.meta.legend.indexOf(legend) ?? -1;
  if (!file || i < 0) {
    return undefined;
  }
  const points: Point[] = [];
  for (const row of file.data) {
    const t = toNumber(row[0]);
    if (t === null) {
      continue;
    }
    const v = toNumber(row[i + 1]);
    points.push([t * 1000, v === null ? null : map ? map(v) : v]);
  }
  return points;
}

// Seconds between rows (meta.step, else from the first two rows).
export function rrdStepMs(file: RrdFile | undefined): number | undefined {
  if (!file) {
    return undefined;
  }
  if (file.meta.step) {
    return file.meta.step * 1000;
  }
  const a = toNumber(file.data[0]?.[0]);
  const b = toNumber(file.data[1]?.[0]);
  return a !== null && b !== null ? (b - a) * 1000 : undefined;
}

export const hasValues = (points: Point[] | undefined): points is Point[] =>
  !!points && points.some((p) => p[1] !== null);

export function latest(points: Point[] | undefined): [number, number] | undefined {
  for (let i = (points?.length ?? 0) - 1; i >= 0; i--) {
    const p = points![i];
    if (p[1] !== null) {
      return p as [number, number];
    }
  }
  return undefined;
}

// The non-null value nearest to t, if one lies within `tolerance` ms.
export function valueAt(points: Point[] | undefined, t: number, tolerance: number): number | undefined {
  if (!points || points.length === 0) {
    return undefined;
  }
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid][0] < t) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  let best: number | undefined;
  let bestD = tolerance;
  for (let i = Math.max(0, lo - 2); i <= Math.min(points.length - 1, lo + 2); i++) {
    const [pt, v] = points[i];
    if (v !== null && Math.abs(pt - t) <= bestD) {
      best = v;
      bestD = Math.abs(pt - t);
    }
  }
  return best;
}

export interface RangeStats {
  min: [number, number];
  max: [number, number];
  avg: number;
  last: [number, number];
}

// Min (with its time), mean, max and the last value over [from, to], ignoring nulls.
export function rangeStats(points: Point[] | undefined, from: number, to: number): RangeStats | undefined {
  let min: [number, number] | undefined;
  let max: [number, number] | undefined;
  let last: [number, number] | undefined;
  let sum = 0;
  let n = 0;
  for (const [t, v] of points ?? []) {
    if (v === null || t < from || t > to) {
      continue;
    }
    if (!min || v < min[1]) min = [t, v];
    if (!max || v > max[1]) max = [t, v];
    last = [t, v];
    sum += v;
    n++;
  }
  return min && max && last ? { min, max, avg: sum / n, last } : undefined;
}

// The value `ms` before the latest one (for "↓ 0.3° last hour"), from the nearest row.
export function change(points: Point[] | undefined, ms: number, tolerance: number): number | undefined {
  const now = latest(points);
  const before = now ? valueAt(points, now[0] - ms, tolerance) : undefined;
  return now && before !== undefined ? now[1] - before : undefined;
}
